import React, { useState, useEffect } from "react";
import { useLang } from "@/lib/i18n";
import { formatCurrency, formatDateDDMMYYYY } from "@/lib/format";
import { CATEGORIES } from "@/lib/calcEngine";
import {
  orderedItems, expandLaddoo, getLaddooRecipe, itemLabel, laddooIngLabel,
  ladQtyPerLaddoo, ladQtyTotal,
} from "@/lib/laddoo";
import { CalendarDays, Users, IndianRupee, TrendingUp, Wheat, Cookie, ChevronDown } from "lucide-react";

const CAT_COLOR = { balvatika: "#DB2777", primary: "#2563EB", upper_primary: "#059669", high_school: "#EA580C" };
const LADDOO_BG = "#FFF4E6";

function CountUpNumber({ value, duration = 900, render }) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setV(value); return; }
    let raf; const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick); else setV(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{render(v)}</>;
}

function qty(q, unit) {
  if (unit === "g" && q >= 1000) return `${(q / 1000).toFixed(2)} kg`;
  return `${q} ${unit}`;
}

const PV_ALL = { show_item_rates: true, show_item_amounts: true, show_cost_per_student: true, show_laddoo_subtotal: true };

function LaddooMobileGroup({ studentCount, cost, pv }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const exp = expandLaddoo(studentCount, cost, getLaddooRecipe());
  const showSub = pv.show_laddoo_subtotal && exp.subtotal != null;
  return (
    <div className="p-3" style={{ background: LADDOO_BG }} data-testid="laddoo-mobile-group">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between font-semibold text-thali-ink" data-testid="laddoo-mobile-toggle">
        <span>🧁 {lang === "or" ? "ପୁଷ୍ଟିକର ଲଡୁ" : "Nutrition Laddoo"} ({exp.rows.length})</span>
        <span className="flex items-center gap-1 font-num">{showSub ? formatCurrency(exp.subtotal) : ""} <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {exp.estimated && pv.show_item_rates && <div className="text-[10px] text-thali-saffron font-semibold mt-1">Estimated breakdown</div>}
      {open && (
        <div className="mt-2 space-y-2" data-testid="laddoo-mobile-rows">
          {exp.rows.map((lr) => (
            <div key={lr.key} className="rounded-lg bg-white/70 p-2">
              <div className="flex justify-between font-semibold text-sm">
                <span>{lr.emoji} {laddooIngLabel(lr, lang)}</span>
                {pv.show_item_amounts && lr.amount != null && <span className="font-num">{formatCurrency(lr.amount)}</span>}
              </div>
              <div className="text-[11px] text-thali-ink/60">
                {pv.show_item_rates && lr.ratePerLaddoo != null && <>{t("rate")} {formatCurrency(lr.ratePerLaddoo)} · </>}{ladQtyPerLaddoo(lr.gramsPerLaddoo)} · {t("total_qty")} {lr.key === "making" ? `1 no × ${studentCount}` : ladQtyTotal(lr.totalG)}
              </div>
            </div>
          ))}
          {showSub && (
            <div className="flex justify-between font-bold text-sm pt-1 border-t border-thali-turmeric/30">
              <span>Laddoo subtotal</span><span className="font-num">{formatCurrency(exp.subtotal)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LaddooDesktopRows({ studentCount, cost, pv, cols }) {
  const { lang } = useLang();
  const exp = expandLaddoo(studentCount, cost, getLaddooRecipe());
  const showSub = pv.show_laddoo_subtotal && exp.subtotal != null;
  return (
    <>
      <tr style={{ background: LADDOO_BG }}>
        <td className="p-2 pl-4 font-bold" colSpan={cols} data-testid="laddoo-heading-row">
          🧁 Nutrition Laddoo{showSub && cost != null ? ` — ${formatCurrency(cost)} per student` : ""}
          {exp.estimated && pv.show_item_rates && <span className="ml-2 text-xs text-thali-saffron">· Estimated breakdown</span>}
        </td>
      </tr>
      {exp.rows.map((lr) => (
        <tr key={lr.key} style={{ background: LADDOO_BG }} className="border-t border-thali-turmeric/10">
          <td className="p-2 pl-8 font-medium">{lr.emoji} {laddooIngLabel(lr, lang)}</td>
          <td className="p-2 text-center">{ladQtyPerLaddoo(lr.gramsPerLaddoo)}</td>
          {pv.show_item_rates && <td className="p-2 text-center">{lr.ratePerLaddoo != null ? formatCurrency(lr.ratePerLaddoo) : "—"}</td>}
          <td className="p-2 text-center">{lr.key === "making" ? `1 no × ${studentCount}` : ladQtyTotal(lr.totalG)}</td>
          {pv.show_item_amounts && <td className="p-2 pr-4 text-right font-semibold">{lr.amount != null ? formatCurrency(lr.amount) : "—"}</td>}
        </tr>
      ))}
      {showSub && (
        <tr style={{ background: LADDOO_BG }} className="border-t border-thali-turmeric/20">
          <td className="p-2 pl-4 font-bold" colSpan={cols - 1} data-testid="laddoo-subtotal-row">Laddoo subtotal</td>
          <td className="p-2 pr-4 text-right font-bold font-num">{formatCurrency(exp.subtotal)}</td>
        </tr>
      )}
    </>
  );
}

function CategoryCard({ ckey, data, pv }) {
  const { t, lang } = useLang();
  const color = CAT_COLOR[ckey];
  const items = orderedItems(data.items);
  const lad = data.items["Laddoo"];
  const cols = 3 + (pv.show_item_rates ? 1 : 0) + (pv.show_item_amounts ? 1 : 0);
  const mcols = cols === 5 ? "grid-cols-4" : cols === 4 ? "grid-cols-3" : "grid-cols-2";
  return (
    <div className="rounded-card bg-thali-paper shadow-warm overflow-hidden border border-thali-turmeric/20" data-testid={`result-cat-${ckey}`}>
      <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: color }}>
        <span className="font-heading font-bold">{t(ckey)} · {data.student_count}</span>
        <span className="font-num font-bold">{formatCurrency(data.subtotal)}</span>
      </div>
      {/* Desktop table */}
      <table className="w-full hidden md:table text-sm">
        <thead>
          <tr className="bg-thali-cream text-thali-ink/70">
            <th className="text-left p-2 pl-4">{t("item")}</th><th className="p-2">{t("per_student")}</th>
            {pv.show_item_rates && <th className="p-2">{t("rate")}</th>}<th className="p-2">{t("total_qty")}</th>{pv.show_item_amounts && <th className="p-2 pr-4 text-right">{t("amount")}</th>}
          </tr>
        </thead>
        <tbody>
          {items.map(([name, it]) => (
            <tr key={name} className="border-t border-thali-turmeric/10">
              <td className="p-2 pl-4 font-medium">{it.emoji} {itemLabel(name, lang)}{it.is_auto_balanced && pv.show_item_rates && <span className="ml-1 text-xs bg-thali-turmeric/40 rounded px-1">⚖️ {t("auto")}</span>}</td>
              <td className="p-2 text-center">{qty(it.qty_per_student, it.unit)}</td>
              {pv.show_item_rates && <td className="p-2 text-center">{formatCurrency(it.rate_per_student)}</td>}
              <td className="p-2 text-center">{qty(it.total_quantity, it.unit)}</td>
              {pv.show_item_amounts && <td className="p-2 pr-4 text-right font-semibold">{formatCurrency(it.total_cost)}</td>}
            </tr>
          ))}
          {lad && <LaddooDesktopRows studentCount={data.student_count} cost={lad.rate_per_student} pv={pv} cols={cols} />}
        </tbody>
      </table>
      {/* Mobile stacked */}
      <div className="md:hidden divide-y divide-thali-turmeric/10">
        {items.map(([name, it]) => (
          <div key={name} className="p-3">
            <div className="font-semibold flex items-center gap-1">{it.emoji} {itemLabel(name, lang)}{it.is_auto_balanced && pv.show_item_rates && <span className="text-xs bg-thali-turmeric/40 rounded px-1">⚖️ {t("auto")}</span>}</div>
            <div className={`grid ${mcols} gap-1 mt-1 text-xs text-thali-ink/70`}>
              <div><div className="text-[10px]">{t("per_student")}</div><div className="font-semibold text-thali-ink">{qty(it.qty_per_student, it.unit)}</div></div>
              {pv.show_item_rates && <div><div className="text-[10px]">{t("rate")}</div><div className="font-semibold text-thali-ink">{formatCurrency(it.rate_per_student)}</div></div>}
              <div><div className="text-[10px]">{t("total_qty")}</div><div className="font-semibold text-thali-ink">{qty(it.total_quantity, it.unit)}</div></div>
              {pv.show_item_amounts && <div className="text-right"><div className="text-[10px]">{t("amount")}</div><div className="font-bold text-thali-ink">{formatCurrency(it.total_cost)}</div></div>}
            </div>
          </div>
        ))}
        {lad && <LaddooMobileGroup studentCount={data.student_count} cost={lad.rate_per_student} pv={pv} />}
      </div>
      {pv.show_cost_per_student && data.per_student_total != null && (
        <div className="px-4 py-2 bg-thali-cream flex justify-between text-sm">
          <span>{t("per_student")}</span><span className="font-num font-bold">{formatCurrency(data.per_student_total)}</span>
        </div>
      )}
    </div>
  );
}

function AggregatedSummary({ aggregated, pv }) {
  const { t, lang } = useLang();
  const items = orderedItems(aggregated);
  const lad = aggregated["Laddoo"];
  const ladExp = lad ? expandLaddoo(lad.total_quantity, lad.avg_rate, getLaddooRecipe()) : null;
  const cols = 2 + (pv.show_item_rates ? 1 : 0) + (pv.show_item_amounts ? 1 : 0);
  return (
    <div className="rounded-card bg-thali-paper shadow-warm overflow-hidden border border-thali-turmeric/20">
      <div className="px-4 py-3 bg-thali-ink text-white font-heading font-bold">{t("ingredients_summary")}</div>
      <table className="w-full text-sm">
        <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">{t("item")}</th><th className="p-2">{t("total_qty")}</th>{pv.show_item_rates && <th className="p-2">Avg {t("rate")}</th>}{pv.show_item_amounts && <th className="p-2 pr-4 text-right">{t("amount")}</th>}</tr></thead>
        <tbody>
          {items.map(([name, it]) => (
            <tr key={name} className="border-t border-thali-turmeric/10">
              <td className="p-2 pl-4 font-medium">{it.emoji} {itemLabel(name, lang)}</td>
              <td className="p-2 text-center">{qty(it.total_quantity, it.unit)}</td>
              {pv.show_item_rates && <td className="p-2 text-center">{formatCurrency(it.avg_rate)}</td>}
              {pv.show_item_amounts && <td className="p-2 pr-4 text-right font-semibold">{formatCurrency(it.total_cost)}</td>}
            </tr>
          ))}
          {ladExp && (
            <>
              <tr style={{ background: LADDOO_BG }}>
                <td className="p-2 pl-4 font-bold" colSpan={cols}>🧁 Nutrition Laddoo{pv.show_laddoo_subtotal && lad.avg_rate != null ? ` — ${formatCurrency(lad.avg_rate)} per student` : ""}</td>
              </tr>
              {ladExp.rows.map((lr) => (
                <tr key={lr.key} style={{ background: LADDOO_BG }} className="border-t border-thali-turmeric/10">
                  <td className="p-2 pl-8 font-medium">{lr.emoji} {laddooIngLabel(lr, lang)}</td>
                  <td className="p-2 text-center">{lr.key === "making" ? `1 no × ${lad.total_quantity}` : ladQtyTotal(lr.totalG)}</td>
                  {pv.show_item_rates && <td className="p-2 text-center">{lr.ratePerLaddoo != null ? formatCurrency(lr.ratePerLaddoo) : "—"}</td>}
                  {pv.show_item_amounts && <td className="p-2 pr-4 text-right font-semibold">{lr.amount != null ? formatCurrency(lr.amount) : "—"}</td>}
                </tr>
              ))}
              {pv.show_laddoo_subtotal && ladExp.subtotal != null && (
                <tr style={{ background: LADDOO_BG }} className="border-t border-thali-turmeric/20">
                  <td className="p-2 pl-4 font-bold" colSpan={cols - 1}>Laddoo subtotal</td>
                  <td className="p-2 pr-4 text-right font-bold font-num">{formatCurrency(ladExp.subtotal)}</td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function ResultDisplay({ result, animate = false }) {
  const { t } = useLang();
  if (!result) return null;
  const s = result.summary;
  const pv = result.privacy || PV_ALL;
  const cards = [
    { icon: CalendarDays, label: t("date"), value: formatDateDDMMYYYY(result.date), c: "#2563EB" },
    { icon: CalendarDays, label: t("day"), value: result.weekday_name, c: "#059669" },
    { icon: Users, label: t("students"), value: s.total_students, c: "#DB2777", anim: { value: s.total_students, fmt: (v) => Math.round(v) } },
    ...(pv.show_cost_per_student && s.avg_per_student != null ? [{ icon: TrendingUp, label: t("avg_student"), value: formatCurrency(s.avg_per_student), c: "#EA580C" }] : []),
    { icon: Wheat, label: t("rice_kg"), value: `${s.total_rice_kg} kg`, c: "#D97706", anim: { value: s.total_rice_kg, fmt: (v) => `${v.toFixed(1)} kg` } },
  ];
  return (
    <div className="space-y-4" data-testid="result-display">
      <div className="rounded-card bg-thali-green text-white p-5 flex items-center justify-between shadow-warm">
        <div>
          <div className="text-sm opacity-80 flex items-center gap-1"><IndianRupee className="w-4 h-4" /> {t("grand_total")}</div>
          <div className="text-4xl font-num font-extrabold" data-testid="result-grand-total">
            {animate ? <CountUpNumber value={s.grand_total} render={(v) => formatCurrency(v)} /> : formatCurrency(s.grand_total)}
          </div>
          {result.laddoo_active && <div className="text-xs mt-1 bg-white/20 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Cookie className="w-3 h-3" /> {t("laddoo_day")}</div>}
        </div>
        <div className="text-right text-sm opacity-90">
          <div>{result.menu_display}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {cards.map((c, i) => (
          <div key={i} className="rounded-card bg-thali-paper border-l-4 shadow-warm p-3" style={{ borderColor: c.c }}>
            <div className="text-xs text-thali-ink/60">{c.label}</div>
            <div className="font-num font-bold text-lg text-thali-ink">
              {animate && c.anim ? <CountUpNumber value={c.anim.value} render={c.anim.fmt} /> : c.value}
            </div>
          </div>
        ))}
      </div>

      {CATEGORIES.filter((c) => result.categories[c].student_count > 0).map((c) => (
        <CategoryCard key={c} ckey={c} data={result.categories[c]} pv={pv} />
      ))}

      <AggregatedSummary aggregated={result.aggregated_items} pv={pv} />
    </div>
  );
}
